import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const AddEmployee = () => {
  const emptyFormData = {
    company: "",
    teamMemberName: "",
    mobileNumber: "",
    emergencyMobileNumber: "",
    email: "",
    password: "",
    salary: "",
    pfEnabled: false, // PF deduction enabled/disabled
    pfPercentage: 12, // Default PF percentage (12%)
    esicEnabled: false, // ESIC deduction enabled/disabled
    esicPercentage: 3.25, // Default ESIC percentage (3.25%)
    dateOfJoining: "",
    shift: "",
    departments: [], // Changed from department to departments (array)
    designation: [], // Changed from designation to designation (array)
    role: "",
    aadharNumber: "",
    adharImage: null,
    adharImagePreview: null,
    panNumber: "",
    userUpi: "",
    profileImage: null,
    profileImagePreview: null,
    panImage: null,
    panImagePreview: null,
    weeklyHoliday: [],
    address: "",
    // New fields from API specification
    paidLeaves: [], // Array of {type: String, count: Number}
    documents: [], // Array of {type: String, url: String, publicId: String, uploadedAt: Date}
  };

  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [formData, setFormData] = useState(emptyFormData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPanImageModalOpen, setIsPanImageModalOpen] = useState(false);
  const [isAadharImageModalOpen, setIsAadharImageModalOpen] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]); // For paid leaves dropdown - initialized as empty array
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false); // Loading state for leave types
  const [newPaidLeave, setNewPaidLeave] = useState({ type: '', count: 0 }); // For adding paid leaves
  const [newDocument, setNewDocument] = useState({ type: '', file: null }); // For adding documents

  // Validation Error State
  const [validationErrors, setValidationErrors] = useState({
    mobileNumber: '',
    emergencyMobileNumber: '',
    aadharNumber: '',
    panNumber: '',
    userUpi: '',
    salary: '',
    teamMemberName: ''
  });

  // User Access Control State - Updated to match API specification
  const [userAccess, setUserAccess] = useState({
    dashboard: false,
    leads_dashboard: false,
    leads_leads: false,
    leads_customer: false,
    leads_product: false,
    lead_report: false,
    leads_workflow: false,
    list_task: false,
    create_task: false,
    update_task: false,
    delete_task: false,
    shifted_task_history: false,
    shift_task: false,
    create_credit_points: false,
    credit_points: false,
    settings: false,
    settings_category: false,
    settings_shift: false,
    settings_leads_status: false,
    settings_customer_group: false,
    settings_leave_type: false,
    reports: false,
    followup_report: false,
    task_report: false,
    leads_report: false,
    attendance_report: false,
    leave_report: false,
    payment_report: false,
    accounts: false,
    list_customer: false,
    create_customer: false,
    update_customer: false,
    delete_customer: false,
    list_teammember: false,
    create_teammember: false,
    update_teammember: false,
    delete_teammember: false,
    attendance: false,
    list_leave: false,
    create_leave: false,
    update_leave: false,
    delete_leave: false,
    list_franchise: false,
    create_franchise: false,
    update_franchise: false,
    delete_franchise: false,
    list_business: false,
    create_business: false,
    update_business: false,
    delete_business: false,
    chat: false,
    list_department: false,
    create_department: false,
    update_department: false,
    delete_department: false,
    salary_generation: false,
    todo_list: false,
    user_profile: false,
    business_profile: false,
    list_project_manager: false,
    create_project_manager: false,
    update_project_manager: false,
    delete_project_manager: false,
    customer_support: false,
    com_tickets: false,
    sup_tickets: false,
    employee_performance: false
  });

  // Options for dropdowns
  const roles = ["Manager", "Employee"];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  
  // Helper functions for new fields
  const addPaidLeave = () => {
    if (newPaidLeave.type && newPaidLeave.count > 0) {
      // Check if this leave type already exists
      const existingLeave = formData.paidLeaves.find(leave => leave.type === newPaidLeave.type);
      if (existingLeave) {
        alert('This leave type is already added. Please remove it first or choose a different type.');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        paidLeaves: [...(prev.paidLeaves || []), { ...newPaidLeave }]
      }));
      setNewPaidLeave({ type: '', count: 0 });
    } else {
      alert('Please select a leave type and enter a count greater than 0.');
    }
  };
  
  const removePaidLeave = (index) => {
    setFormData(prev => ({
      ...prev,
      paidLeaves: (prev.paidLeaves || []).filter((_, i) => i !== index)
    }));
  };
  
  const addDocument = () => {
    if (newDocument.type && newDocument.file) {
      // Check if this document type already exists
      const existingDoc = formData.documents.find(doc => doc.type === newDocument.type);
      if (existingDoc) {
        alert('This document type is already added. Please remove it first or choose a different type.');
        return;
      }
      
      const documentEntry = {
        type: newDocument.type,
        file: newDocument.file,
        uploadedAt: new Date().toISOString()
      };
      setFormData(prev => ({
        ...prev,
        documents: [...(prev.documents || []), documentEntry]
      }));
      setNewDocument({ type: '', file: null });
    } else {
      alert('Please enter a document type and select a file.');
    }
  };
  
  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).filter((_, i) => i !== index)
    }));
  };

  // Function to convert 24-hour time to 12-hour format
  const convertTo12Hour = (time24) => {
    if (!time24) return 'N/A';

    try {
      const [hours, minutes] = time24.split(':');
      const hour = parseInt(hours, 10);
      const minute = minutes || '00';

      if (hour === 0) {
        return `12:${minute} AM`;
      } else if (hour < 12) {
        return `${hour}:${minute} AM`;
      } else if (hour === 12) {
        return `12:${minute} PM`;
      } else {
        return `${hour - 12}:${minute} PM`;
      }
    } catch (error) {
      console.error('Error converting time:', error);
      return time24; // Return original time if conversion fails
    }
  };

const getDepartmentNames = (deptIdsOrId) => {
  if (!deptIdsOrId) return ['N/A'];

  let ids = [];
  if (Array.isArray(deptIdsOrId)) {
    ids = deptIdsOrId;
  } else if (deptIdsOrId) {
    ids = [deptIdsOrId];
  }

  if (departments.length === 0) {
    if (ids.length > 0) {
      console.warn('Departments not loaded yet. Raw IDs:', ids); // Debug log
      return ids.map(id => `ID: ${String(id)}`); // Safe string conversion
    }
    return ['Loading departments...'];
  }

  const stringIds = ids.map(id => String(id).trim());

  const names = stringIds.slice(0, 2).map(idStr => {
    const trimmedId = idStr; // Already trimmed
    const dept = departments.find(d => String(d._id).trim() === trimmedId);
    if (dept) {
      return dept.name;
    } else {
      console.warn(`Department ID not found: ${trimmedId}. Available depts:`, departments.map(d => String(d._id))); // Debug log
      return `Unknown (${trimmedId})`; // Fallback: show ID if name not found
    }
  });

  if (stringIds.length > 2) {
    names.push(`+${stringIds.length - 2} more`);
  }

  return names;
};


const getDesignationNames = (desigIdsOrId) => {
  if (!desigIdsOrId) return ['N/A'];

  let ids = [];
  if (Array.isArray(desigIdsOrId)) {
    ids = desigIdsOrId;
  } else if (desigIdsOrId) {
    ids = [desigIdsOrId];
  }

  if (designations.length === 0) {
    if (ids.length > 0) {
      console.warn('Designations not loaded yet. Raw IDs:', ids); // Debug log
      return ids.map(id => `ID: ${String(id)}`); 
    }
    return ['Loading designations...'];
  }

  // Convert all IDs to strings for safe comparison
  const stringIds = ids.map(id => String(id).trim());

  const names = stringIds.slice(0, 2).map(idStr => {
    const trimmedId = idStr; // Already trimmed
    const desig = designations.find(d => String(d._id).trim() === trimmedId);
    if (desig) {
      return desig.name;
    } else {
      console.warn(`Designation ID not found: ${trimmedId}. Available desigs:`, designations.map(d => String(d._id))); // Debug log
      return `Unknown (${trimmedId})`;
    }
  });

  if (stringIds.length > 2) {
    names.push(`+${stringIds.length - 2} more`);
  }

  return names;
};


  // Initial chained loading (async)
useEffect(() => {
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Step 1: Fetch companies first (sets selectedCompanyId and localStorage)
      await fetchCompanies();

      // Step 2: Now that companyId is set, fetch departments
      if (selectedCompanyId) {
        await fetchDepartments(selectedCompanyId);
      }

      // Step 3: Fetch shifts (independent)
      await fetchShifts();
      
      // Step 3.5: Fetch leave types (independent) - only if companyId is available
      if (selectedCompanyId) {
        await fetchLeaveTypes();
      }

      // Step 4: Fetch employees (now departments are ready for name lookup)
      await fetchEmployees();

      // Step 5: Fetch designations after departments
      await fetchDesignations();
    } catch (err) {
      console.error('Error in initial data load:', err);
      setError('Failed to load initial data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  loadInitialData();
}, []); // Empty dependency: runs once on mount


  useEffect(() => {
    if (selectedCompanyId) {
      fetchDepartments(selectedCompanyId);
      fetchDesignations();
      fetchLeaveTypes(); // Fetch leave types when company changes
    } else {
      setDepartments([]);
      setDesignations([]);
      setLeaveTypes([]);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (formData.departments && formData.departments.length > 0) {
      fetchDesignations();
    } else {
      setDesignations([]);
    }
  }, [formData.departments]);


  const fetchCompanies = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
      return;
    }

    // Pull companyId (depends on your token structure)
    const companyId = decoded.companyId || decoded.id;
    if (!companyId) {
      setError("No companyId found in token.");
      return;
    }

    try {
      // Fetch the company directly using the decoded companyId
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/companies/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const company = response.data;

      // Save to state
      setCompanies([company]);
      setSelectedCompanyId(company._id);
      setFormData(prev => ({ ...prev, company: company._id }));

      // Optional: keep name in localStorage (if you use it elsewhere)
      localStorage.setItem("selectedCompanyId", company._id);
      localStorage.setItem("selectedCompanyName", company.businessName);

    } catch (err) {
      console.error("Error fetching company:", err.response?.data || err.message);
      setError(
        `Failed to fetch company: ${err.response?.data?.message || err.message || "Unable to connect to server"
        }`
      );
    }
  };

  // Re-render table when employees or departments change (ensures names update)
    useEffect(() => {
      if (employees.length > 0 && departments.length > 0) {
        console.log('Table refresh: Employees and departments updated'); // Debug
      }
    }, [employees, departments]);



const fetchEmployees = async () => {
  const token = localStorage.getItem("authToken");
  const companyId = localStorage.getItem("selectedCompanyId");

  if (!token) {
    setError("No access token found. Please log in.");
    setLoading(false);
    return;
  }

  if (!companyId) {
    setError("No company selected. Please select a company first.");
    setLoading(false);
    return;
  }

  // If departments are empty, warn and potentially refetch later (but since we chain, it should be loaded)
  if (departments.length === 0) {
    console.warn('⚠️ Departments not loaded yet. Table may show raw IDs initially.');
  }

  try {
    console.log('📡 Making API request to fetch employees...');
    const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/employees/company/${companyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });


    // Enhanced logging for each employee's departments and access permissions
    if (response.data && Array.isArray(response.data)) {
      response.data.forEach((employee, index) => {
       
        if (Array.isArray(employee.departments) && employee.departments.length > 0) {
         
        } else if (employee.department) {
        
        } else {
        }
        const hasPermissions = employee.userAccess ||
          employee.accessPermissions ||
          employee.permissions ||
          employee.access;
      });
    }

    setEmployees(response.data);

  } catch (err) {
    console.error("❌ Error fetching employees:", err.response?.data || err.message);
    console.error("├── Error status:", err.response?.status);
    console.error("├── Error details:", err.response?.data);

    if (err.response?.status === 401) {
      console.log('🔄 Token expired, cleaning up...');
      localStorage.removeItem("authToken");
      setError("Session expired. Please log in again.");
      window.location.href = '/login';
      return;
    }

    setError(`Failed to fetch employees: ${err.response?.data?.message || err.message || "Unable to connect to server"}`);
  } finally {
    setLoading(false);
    console.log('🏁 Fetch employees process completed');
  }
};


const fetchDepartments = async (companyId) => {
  if (!companyId) return;

  const token = localStorage.getItem("authToken");
  try {
    const response = await axios.get(
      `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setDepartments(response.data);
    console.log('✅ Departments fetched:', response.data.length, 'items'); // Debug
  } catch (err) {
    console.error("Error fetching departments:", err.response?.data || err.message);
    setDepartments([]);
  }
};


  const fetchShifts = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.warn("⚠️ No access token found for fetching shifts. Using local fallback.");
      return;
    }

    try {
      const response = await axios.get(
        'https://customersupport-jhy9.onrender.com/api/shifts/',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShifts(response.data);
    } catch (err) {
      console.error("❌ Error fetching shifts:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.warn("🔐 Authentication error for shifts. Using local fallback.");
        setShifts([]);
      } else {
        setError("Failed to fetch shifts. Using default shifts.");
      }
    }
  };
  
  const fetchLeaveTypes = async (companyIdParam = null) => {
    setLoadingLeaveTypes(true);
    // Try all possible token keys used across app
    const token =
      localStorage.getItem("authToken") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("token");

    let companyId = companyIdParam || localStorage.getItem("selectedCompanyId") || formData.company;

    // If no companyId yet, try decoding from token
    if (!companyId && token) {
      try {
        const decoded = jwtDecode(token);
        companyId =
          decoded.companyId ||
          decoded.company?._id ||
          decoded.company ||
          decoded.orgId ||
          decoded.organizationId ||
          decoded.id;
      } catch (e) {
        console.warn("⚠️ Error decoding token for companyId in fetchLeaveTypes:", e);
      }
    }

    if (!companyId) {
      console.warn("⚠️ No company ID available for fetching leave types. Using default list.");
      setLeaveTypes([
        { _id: 'casual', leaveType: 'Casual Leave' },
        { _id: 'sick', leaveType: 'Sick Leave' },
        { _id: 'annual', leaveType: 'Annual Leave' }
      ]);
      setLoadingLeaveTypes(false);
      return;
    }

    if (!token) {
      console.warn("⚠️ No token found for fetching leave types. Using default list.");
      setLeaveTypes([
        { _id: 'casual', leaveType: 'Casual Leave' },
        { _id: 'sick', leaveType: 'Sick Leave' },
        { _id: 'annual', leaveType: 'Annual Leave' }
      ]);
      setLoadingLeaveTypes(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/leave-types/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (Array.isArray(response.data?.leaveTypes)) {
        data = response.data.leaveTypes;
      } else if (Array.isArray(response.data?.data)) {
        data = response.data.data;
      } else {
        console.warn("Unexpected leave types response format:", response.data);
        data = [];
      }

      setLeaveTypes(data);
      console.log('✅ Leave types fetched:', data.length, 'items');
    } catch (err) {
      console.error("❌ Error fetching leave types:", err);
      // Provide sensible defaults if API fails
      setLeaveTypes([
        { _id: 'casual', leaveType: 'Casual Leave' },
        { _id: 'sick', leaveType: 'Sick Leave' },
        { _id: 'annual', leaveType: 'Annual Leave' }
      ]);
    } finally {
      setLoadingLeaveTypes(false);
    }
  };

  const fetchDesignations = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.error("No access token found for fetching designations.");
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      localStorage.removeItem("authToken");
      setError("Invalid token. Please log in again.");
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/designations/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Designations API response:", response.data);

      // Handle different response structures
      let designationsData = [];
      if (Array.isArray(response.data)) {
        designationsData = response.data;
      } else if (response.data && Array.isArray(response.data.designations)) {
        designationsData = response.data.designations;
      } else if (response.data && Array.isArray(response.data.data)) {
        designationsData = response.data.data;
      }

      // Filter designations by selected departments if departments are selected
      if (formData.departments && formData.departments.length > 0) {
        designationsData = designationsData.filter(designation =>
          formData.departments.includes(designation.department?._id) ||
          formData.departments.includes(designation.department)
        );
      }

      setDesignations(designationsData);
    } catch (err) {
      console.error("Error fetching designations:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setDesignations([]);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    setFormData(prev => ({ ...prev, company: companyId, departments: [] }));
  };

  // Validation Functions
  const validateMobileNumber = (value) => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue.length === 0) {
      return { isValid: true, message: '', cleanValue: '' };
    }
    
    if (numericValue.length > 10) {
      return { isValid: false, message: 'Mobile number cannot exceed 10 digits', cleanValue: numericValue.slice(0, 10) };
    }
    
    if (numericValue.length < 10 && numericValue.length > 0) {
      return { isValid: false, message: 'Please enter a valid 10-digit mobile number', cleanValue: numericValue };
    }
    
    if (numericValue.length === 10) {
      return { isValid: true, message: '', cleanValue: numericValue };
    }
    
    return { isValid: false, message: 'Please enter only numbers', cleanValue: numericValue };
  };

  const validateAadharNumber = (value) => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue.length === 0) {
      return { isValid: true, message: '', cleanValue: '' };
    }
    
    if (numericValue.length > 12) {
      return { isValid: false, message: 'Aadhar number cannot exceed 12 digits', cleanValue: numericValue.slice(0, 12) };
    }
    
    if (numericValue.length < 12 && numericValue.length > 0) {
      return { isValid: false, message: 'Please enter a valid 12-digit Aadhar number', cleanValue: numericValue };
    }
    
    if (numericValue.length === 12) {
      return { isValid: true, message: '', cleanValue: numericValue };
    }
    
    return { isValid: false, message: 'Please enter only numbers', cleanValue: numericValue };
  };

  const validatePANNumber = (value) => {
    // Convert to uppercase and remove spaces
    const cleanValue = value.toUpperCase().replace(/\s/g, '');
    
    if (cleanValue.length === 0) {
      return { isValid: true, message: '', cleanValue: '' };
    }
    
    // PAN format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    
    if (cleanValue.length > 10) {
      return { isValid: false, message: 'PAN number cannot exceed 10 characters', cleanValue: cleanValue.slice(0, 10) };
    }
    
    if (cleanValue.length === 10) {
      if (panRegex.test(cleanValue)) {
        return { isValid: true, message: '', cleanValue: cleanValue };
      } else {
        return { isValid: false, message: 'Invalid PAN format. Format: ABCDE1234F', cleanValue: cleanValue };
      }
    }
    
    if (cleanValue.length < 10 && cleanValue.length > 0) {
      // Check partial format
      const partialPanRegex = /^[A-Z]{0,5}[0-9]{0,4}[A-Z]{0,1}$/;
      if (partialPanRegex.test(cleanValue)) {
        return { isValid: false, message: 'Please enter complete PAN number (10 characters)', cleanValue: cleanValue };
      } else {
        return { isValid: false, message: 'Invalid PAN format. Use only letters and numbers', cleanValue: cleanValue };
      }
    }
    
    return { isValid: false, message: 'Invalid PAN format. Format: ABCDE1234F', cleanValue: cleanValue };
  };

  const validateUPI = (value) => {
    // Remove spaces
    const cleanValue = value.toLowerCase().replace(/\s/g, '');
    
    if (cleanValue.length === 0) {
      return { isValid: true, message: '', cleanValue: '' };
    }
    
    // UPI format: username@bankname (e.g., user@paytm, 9876543210@ybl)
    const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/;
    
    if (cleanValue.includes('@')) {
      if (upiRegex.test(cleanValue)) {
        const parts = cleanValue.split('@');
        if (parts[0].length > 0 && parts[1].length > 0) {
          return { isValid: true, message: '', cleanValue: cleanValue };
        }
      }
      return { isValid: false, message: 'Invalid UPI format. Format: username@bankname', cleanValue: cleanValue };
    } else {
      if (cleanValue.length > 0) {
        return { isValid: false, message: 'UPI ID must contain @ symbol. Format: username@bankname', cleanValue: cleanValue };
      }
    }
    
    return { isValid: true, message: '', cleanValue: cleanValue };
  };

  const validateSalary = (value) => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    if (numericValue.length === 0) {
      return { isValid: true, message: '', cleanValue: '' };
    }
    
    // Check for multiple decimal points
    const decimalCount = (numericValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return { isValid: false, message: 'Please enter a valid salary amount', cleanValue: numericValue.substring(0, numericValue.lastIndexOf('.')) };
    }
    
    // Validate numeric format
    const salaryRegex = /^[0-9]+(\.[0-9]{0,2})?$/;
    if (numericValue.length > 0 && !salaryRegex.test(numericValue)) {
      return { isValid: false, message: 'Please enter only numbers for salary', cleanValue: numericValue.replace(/[^0-9.]/g, '') };
    }
    
    // Check for reasonable salary range (optional)
    const salaryAmount = parseFloat(numericValue);
    if (salaryAmount > 10000000) { // 1 crore limit
      return { isValid: false, message: 'Salary amount seems too high', cleanValue: numericValue };
    }
    
    return { isValid: true, message: '', cleanValue: numericValue };
  };

  const validateTeamMemberName = (value) => {
    // Remove leading/trailing spaces but keep internal spaces
    const cleanValue = value;
    
    if (cleanValue.length === 0) {
      return { isValid: true, message: '', cleanValue: '' };
    }
    
    // Check for numbers in the name
    const hasNumbers = /[0-9]/.test(cleanValue);
    if (hasNumbers) {
      // Remove numbers and return cleaned value
      const nameWithoutNumbers = cleanValue.replace(/[0-9]/g, '');
      return { isValid: false, message: 'Name should not contain numbers', cleanValue: nameWithoutNumbers };
    }
    
    // Check for special characters (allow only letters, spaces, dots, apostrophes, hyphens)
    const nameRegex = /^[a-zA-Z\s.'\-]+$/;
    if (!nameRegex.test(cleanValue)) {
      // Remove invalid characters
      const cleanedName = cleanValue.replace(/[^a-zA-Z\s.'\-]/g, '');
      return { isValid: false, message: 'Name should contain only letters, spaces, and basic punctuation', cleanValue: cleanedName };
    }
    
    // Check minimum length
    if (cleanValue.length < 2) {
      return { isValid: false, message: 'Name should be at least 2 characters long', cleanValue: cleanValue };
    }
    
    // Check maximum length
    if (cleanValue.length > 50) {
      return { isValid: false, message: 'Name should not exceed 50 characters', cleanValue: cleanValue.substring(0, 50) };
    }
    
    return { isValid: true, message: '', cleanValue: cleanValue };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    let newErrors = { ...validationErrors };

    // Apply validation based on field type
    if (name === 'mobileNumber' || name === 'emergencyMobileNumber') {
      const validation = validateMobileNumber(value);
      processedValue = validation.cleanValue;
      newErrors[name] = validation.message;
    } else if (name === 'aadharNumber') {
      const validation = validateAadharNumber(value);
      processedValue = validation.cleanValue;
      newErrors[name] = validation.message;
    } else if (name === 'panNumber') {
      const validation = validatePANNumber(value);
      processedValue = validation.cleanValue;
      newErrors[name] = validation.message;
    } else if (name === 'userUpi') {
      const validation = validateUPI(value);
      processedValue = validation.cleanValue;
      newErrors[name] = validation.message;
    } else if (name === 'salary') {
      const validation = validateSalary(value);
      processedValue = validation.cleanValue;
      newErrors[name] = validation.message;
    } else if (name === 'teamMemberName') {
      const validation = validateTeamMemberName(value);
      processedValue = validation.cleanValue;
      newErrors[name] = validation.message;
    }

    // Update validation errors
    setValidationErrors(newErrors);

    // If departments change, clear designation and fetch new designations
    if (name === 'departments') {
      setFormData({ ...formData, [name]: processedValue, designation: '' });
    } else {
      setFormData({ ...formData, [name]: processedValue });
    }
  };

const handleDepartmentChange = (e) => {
  const { value, checked } = e.target;
  
  console.log('🏢 DEPARTMENT SELECTION CHANGE:');
  console.log('├── Department ID:', value);
  console.log('├── Action:', checked ? 'SELECTED' : 'DESELECTED');
  console.log('├── Current departments before change:', formData.departments);
  
  // Ensure we start with a valid array
  const currentDepartments = Array.isArray(formData.departments) ? formData.departments : [];
  
  let updatedDepartments;
  if (checked) {
    // Add to array, prevent duplicates and ensure valid values
    const validValue = value && value.trim() !== '' ? value.trim() : null;
    if (validValue && !currentDepartments.includes(validValue)) {
      updatedDepartments = [...currentDepartments, validValue];
      console.log('├── Adding department to selection:', validValue);
    } else {
      updatedDepartments = currentDepartments;
      console.log('├── Department already selected or invalid, skipping');
    }
  } else {
    // Remove from array
    updatedDepartments = currentDepartments.filter((dept) => dept !== value);
    console.log('├── Removing department from selection');
  }
  
  console.log('├── Updated departments array:', updatedDepartments);
  console.log('├── Total departments selected:', updatedDepartments.length);
  
  // Find department names for better logging
  const departmentNames = updatedDepartments.map(deptId => {
    const dept = departments.find(d => d._id === deptId);
    return dept ? dept.name : 'Unknown';
  });
  console.log('├── Selected department names:', departmentNames);
  console.log('└── Clearing designation due to department change\n');
  
  setFormData({
    ...formData,
    departments: updatedDepartments, // Always set as array
    designation: [] // Clear designation when departments change (also ensure array)
  });
};

const handleDesignationChange = (e) => {
  const { value, checked } = e.target;
  
  console.log('🎯 DESIGNATION SELECTION CHANGE:');
  console.log('├── Designation ID:', value);
  console.log('├── Action:', checked ? 'SELECTED' : 'DESELECTED');
  console.log('├── Current designations before change:', formData.designation);
  
  let updatedDesignations;
  if (checked) {
    // Add to array, prevent duplicates using Set
    const uniqueDesigs = new Set([...formData.designation, value]);
    updatedDesignations = Array.from(uniqueDesigs);
    console.log('├── Adding designation to selection (unique array)');
  } else {
    // Remove from array
    updatedDesignations = formData.designation.filter((desig) => desig !== value);
    console.log('├── Removing designation from selection');
  }
  
  console.log('├── Updated designations array:', updatedDesignations);
  console.log('├── Total designations selected:', updatedDesignations.length);
  
  // Find designation names for better logging
  const designationNames = updatedDesignations.map(desigId => {
    const desig = designations.find(d => d._id === desigId);
    return desig ? desig.name : 'Unknown';
  });
  console.log('├── Selected designation names:', designationNames);
  console.log('└── Designation change completed\n');
  
  setFormData({
    ...formData,
    designation: updatedDesignations, // Always set as array
  });
};



  const handleDayChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        weeklyHoliday: [...formData.weeklyHoliday, value],
      });
    } else {
      setFormData({
        ...formData,
        weeklyHoliday: formData.weeklyHoliday.filter((day) => day !== value),
      });
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        profileImage: file,
        profileImagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handlePanImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        panImage: file,
        panImagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleAadharImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        adharImage: file,
        adharImagePreview: URL.createObjectURL(file)
      }));
    }
  };

  // Handle permission change
  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    setUserAccess(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handle section-wise select all/deselect all
  const handleSectionSelectAll = (sectionPermissions, checked) => {
    console.log(`🎯 Section Select All - ${checked ? 'Selecting' : 'Deselecting'} permissions:`, sectionPermissions);

    setUserAccess(prev => {
      const updated = { ...prev };
      sectionPermissions.forEach(permission => {
        updated[permission] = checked;
      });

      console.log('├── Updated permissions:', updated);
      return updated;
    });
  };

  // Check if all permissions in a section are selected
  const isSectionAllSelected = (sectionPermissions) => {
    return sectionPermissions.every(permission => userAccess[permission]);
  };

  // Check if some permissions in a section are selected (for indeterminate state)
  const isSectionPartiallySelected = (sectionPermissions) => {
    const selectedCount = sectionPermissions.filter(permission => userAccess[permission]).length;
    return selectedCount > 0 && selectedCount < sectionPermissions.length;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  const token = localStorage.getItem("authToken");
  if (!token) {
    setMessage("❌ Access token not found. Please log in.");
    return;
  }

  // Enhanced validation
  const requiredFields = {
    company: "Company",
    teamMemberName: "Employee Name",
    mobileNumber: "Mobile Number",
    email: "Email",
    designation: "Designation"
  };

  // Check if at least one department is selected
  const validDepartments = formData.departments ? formData.departments.filter(id => id && id.trim() !== '') : [];
  if (validDepartments.length === 0) {
    setMessage("❌ Please select at least one department");
    return;
  }
  if (!formData.designation || formData.designation.length === 0) {
    setMessage("❌ Please select at least one designation");
    return;
  }
 

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    setMessage("❌ Please enter a valid email address");
    return;
  }

 
  // Final validation for required fields
  if (formData.mobileNumber && formData.mobileNumber.length !== 10) {
    setMessage("❌ Mobile number must be exactly 10 digits");
    return;
  }

  if (formData.emergencyMobileNumber && formData.emergencyMobileNumber.length !== 10) {
    setMessage("❌ Emergency mobile number must be exactly 10 digits");
    return;
  }

  if (formData.aadharNumber && formData.aadharNumber.length !== 12) {
    setMessage("❌ Aadhar number must be exactly 12 digits");
    return;
  }

  if (formData.panNumber && formData.panNumber.length !== 10) {
    setMessage("❌ PAN number must be exactly 10 characters");
    return;
  }

  try {
    // Debug log before submission
    console.log('🚀 FORM SUBMISSION DEBUG:');
    console.log('├── formData.departments:', formData.departments);
    console.log('├── departments array type:', Array.isArray(formData.departments));
    console.log('├── departments length:', formData.departments?.length);
    console.log('├── valid departments after filter:', validDepartments);
    console.log('└── proceeding with form submission...\n');
    
    const formDataToSend = new FormData();

    // Append all form data
    Object.keys(formData).forEach((key) => {
      if (key === "weeklyHoliday") {
        formData.weeklyHoliday.forEach((item) => formDataToSend.append(key, item));
      } else if (key === "departments") {
        const validDepts = [...new Set(formData.departments.filter(id => id && id.trim() !== ''))];
        if (validDepts.length === 0) {
          throw new Error('At least one department is required');
        }
        
        // Log department names for clarity
        const departmentNames = validDepts.map(deptId => {
          const dept = departments.find(d => d._id === deptId);
          return dept ? { id: deptId, name: dept.name } : { id: deptId, name: 'Unknown' };
        });
        console.log('📋 Departments being saved:', departmentNames);

        // Try multiple field name patterns for API compatibility
        validDepts.forEach((deptId, index) => {
          formDataToSend.append('departments', deptId);     // Array format
          formDataToSend.append('department', deptId);      // Single format
          formDataToSend.append(`departments[${index}]`, deptId); // Indexed format
        });
        
        // Also add as JSON string for backup
        formDataToSend.append('departmentsJSON', JSON.stringify(validDepts));
        formDataToSend.append('departmentsCount', validDepts.length.toString());
      } else if (key === "designation") {
        const validDesigs = [...new Set(formData.designation.filter(id => id && id.trim() !== ''))];
        if (validDesigs.length === 0) {
          throw new Error('No valid designations to save');
        }
        
        const designationNames = validDesigs.map(desigId => {
          const desig = designations.find(d => d._id === desigId);
          return desig ? { id: desigId, name: desig.name } : { id: desigId, name: 'Unknown' };
        });
        validDesigs.forEach((desigId, index) => {
          formDataToSend.append('designation', desigId);  // Use 'designation' as per API spec
        });
      } else if (key === "paidLeaves") {
        // Handle paid leaves array
        formData.paidLeaves.forEach((leave, index) => {
          formDataToSend.append(`paidLeaves[${index}][type]`, leave.type);
          formDataToSend.append(`paidLeaves[${index}][count]`, leave.count);
        });
      } else if (key === "documents") {
        // Handle documents array - files will be appended separately
        formData.documents.forEach((doc, index) => {
          formDataToSend.append(`documents[${index}][type]`, doc.type);
          if (doc.file) {
            formDataToSend.append(`documents[${index}][file]`, doc.file);
          }
          formDataToSend.append(`documents[${index}][uploadedAt]`, doc.uploadedAt);
        });
      } else if (key === "dateOfJoining" && formData[key]) {
        formDataToSend.append(key, new Date(formData[key]).toISOString());
      } else if (key === "pfEnabled" || key === "esicEnabled") {
        // Handle boolean values for PF and ESIC
        formDataToSend.append(key, formData[key]);
      } else if (key === "pfPercentage" || key === "esicPercentage") {
        // Handle percentage values for PF and ESIC
        formDataToSend.append(key, formData[key]);
      } else if (key === "profileImage" || key === "profileImagePreview" || 
                 key === "panImage" || key === "panImagePreview" || 
                 key === "adharImage" || key === "adharImagePreview") {
        // Skip preview URLs and handle images separately
      } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
        formDataToSend.append(key, formData[key]);
      }
    });

    // Convert userAccess to array of selected permission keys
    const selectedPermissions = Object.entries(userAccess)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    // First remove any existing accessPermissions
    if (formDataToSend.has('accessPermissions')) {
      formDataToSend.delete('accessPermissions');
    }

    // Append each permission individually
    selectedPermissions.forEach(permission => {
      formDataToSend.append('accessPermissions[]', permission);
    });

    // 🖼️ ENHANCED IMAGE PROCESSING FOR DATABASE STORAGE
    console.log('🖼️ PROCESSING IMAGES FOR DATABASE STORAGE:');
    
    // Image validation and compression function
    const processImageForDatabase = (imageFile, imageType) => {
      if (!imageFile) return null;
      
      const maxSize = 5 * 1024 * 1024; // 5MB max size
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      // Validate file type
      if (!allowedTypes.includes(imageFile.type)) {
        console.warn(`⚠️ ${imageType} has invalid type:`, imageFile.type);
        return null;
      }
      
      // Validate file size
      if (imageFile.size > maxSize) {
        console.warn(`⚠️ ${imageType} is too large:`, imageFile.size, 'bytes');
        return null;
      }
      
      console.log(`✓ ${imageType} validated:`, imageFile.name, 'Type:', imageFile.type, 'Size:', imageFile.size);
      return imageFile;
    };
    
    // Profile Image with validation and metadata
    const validatedProfileImage = processImageForDatabase(formData.profileImage, 'Profile Image');
    if (validatedProfileImage) {
      console.log('├── Adding validated profile image to FormData');
      formDataToSend.append('profileImage', validatedProfileImage);
      formDataToSend.append('profileImageName', validatedProfileImage.name);
      formDataToSend.append('profileImageSize', validatedProfileImage.size.toString());
      formDataToSend.append('profileImageType', validatedProfileImage.type);
      formDataToSend.append('profileImageLastModified', validatedProfileImage.lastModified.toString());
    }

    // PAN Image with validation and metadata
    const validatedPanImage = processImageForDatabase(formData.panImage, 'PAN Image');
    if (validatedPanImage) {
      console.log('├── Adding validated PAN image to FormData');
      formDataToSend.append('panImage', validatedPanImage);
      formDataToSend.append('panImageName', validatedPanImage.name);
      formDataToSend.append('panImageSize', validatedPanImage.size.toString());
      formDataToSend.append('panImageType', validatedPanImage.type);
      formDataToSend.append('panImageLastModified', validatedPanImage.lastModified.toString());
    }

    // 🔴 AADHAR IMAGE PROCESSING FOR DATABASE STORAGE (API expects: adharImage: { type: String })
    const validatedAadharImage = processImageForDatabase(formData.adharImage, 'Aadhar Image');
    if (validatedAadharImage) {
      console.log('├── ✅ Processing Aadhar image for database storage');
      console.log('├── API expects field name: "adharImage" with type: String');
      
      // 🔴 PRIMARY API FIELD: adharImage (as specified by API schema)
      formDataToSend.append('adharImage', validatedAadharImage);
      console.log('├── ✅ Added adharImage field for database storage');
      
      // Add metadata for the adharImage field
      formDataToSend.append('adharImageName', validatedAadharImage.name);
      formDataToSend.append('adharImageSize', validatedAadharImage.size.toString());
      formDataToSend.append('adharImageType', validatedAadharImage.type);
      formDataToSend.append('adharImageLastModified', validatedAadharImage.lastModified.toString());
      
      console.log('├── ✅ Aadhar image configured for API field: "adharImage" (String type)');
      console.log('├── Image details: Name=' + validatedAadharImage.name + ', Size=' + validatedAadharImage.size + ', Type=' + validatedAadharImage.type);
    } else {
      console.log('├── ❌ No Aadhar image provided or validation failed');
    }
    
    console.log('└── All images processed with metadata for database storage\n');

    // Log complete FormData after all entries are added
    const formDataEntries = {};
    formDataToSend.forEach((value, key) => {
      if (formDataEntries[key]) {
        // Handle multiple values for same key
        if (Array.isArray(formDataEntries[key])) {
          formDataEntries[key].push(value);
        } else {
          formDataEntries[key] = [formDataEntries[key], value];
        }
      } else {
        formDataEntries[key] = value;
      }
    });
    
    console.log('📋 COMPLETE FORMDATA ENTRIES BEFORE SUBMISSION:');
    console.log('├── Total entries:', Object.keys(formDataEntries).length);
    
    // Filter and display department-related entries
    const departmentEntries = Object.entries(formDataEntries).filter(([key]) => 
      key.toLowerCase().includes('department')
    );
    console.log('├── Department entries found:', departmentEntries.length);
    departmentEntries.forEach(([key, value]) => {
      console.log(`│   ├── ${key}: ${value}`);
    });
    
    // Show image entries with special focus on Aadhar image
    const imageEntries = Object.entries(formDataEntries).filter(([key]) => 
      key.toLowerCase().includes('image')
    );
    console.log('├── Image entries found:', imageEntries.length);
    
    // Specifically check for Aadhar image entries
    const aadharEntries = imageEntries.filter(([key]) => 
      key.toLowerCase().includes('adhar') || key.toLowerCase().includes('aadhar')
    );
    console.log('├── 🔴 AADHAR IMAGE ENTRIES FOUND:', aadharEntries.length);
    
    imageEntries.forEach(([key, value]) => {
      const displayValue = value instanceof File ? `[FILE: ${value.name}]` : value;
      const isAadhar = key.toLowerCase().includes('adhar') || key.toLowerCase().includes('aadhar');
      const prefix = isAadhar ? '🔴 AADHAR' : '│  ';
      console.log(`${prefix} ├── ${key}: ${displayValue}`);
    });
    
    // Show all entries for debugging
    console.log('├── All FormData entries:');
    Object.entries(formDataEntries).forEach(([key, value]) => {
      const displayValue = value instanceof File ? `[FILE: ${value.name}]` : value;
      console.log(`│   ├── ${key}: ${displayValue}`);
    });
    
    console.log('└── FormData ready for submission\n');

    const isEditing = !!editingId;
    const url = isEditing
      ? `https://customersupport-jhy9.onrender.com/api/employees/${editingId}`
      : "https://customersupport-jhy9.onrender.com/api/employees";
    const method = isEditing ? "PUT" : "POST";

   
    
 

    let response;
    let shouldTryJson = false;
    
    try {
      // Try FormData approach first
      console.log('🚀 Attempting FormData submission...');
      response = await axios({
        method,
        url,
        data: formDataToSend,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        validateStatus: function (status) {
          return status >= 200 && status < 500; // Allow 4xx responses to be handled
        }
      });
      
      // Check if we got a department error, if so, try JSON approach
      if (response.status >= 400 && response.data?.message?.includes('department')) {
        console.warn('⚠️ FormData got department error, trying JSON approach...');
        shouldTryJson = true;
      }
      
    } catch (formDataError) {
      console.warn('⚠️ FormData submission failed, trying JSON approach...', formDataError.message);
      shouldTryJson = true;
    }
    
    if (shouldTryJson) {
      console.log('🔄 Creating new FormData with corrected department structure...');
      
      // Create a new FormData with proper department structure for JSON compatibility
      const correctedFormData = new FormData();
      
      // Add all basic fields
      Object.keys(formData).forEach((key) => {
        if (key === "weeklyHoliday") {
          formData.weeklyHoliday.forEach((item) => correctedFormData.append(key, item));
        } else if (key === "departments") {
          // Try different department field structures
          const validDepts = [...new Set(formData.departments.filter(id => id && id.trim() !== ''))];
          
          // Add as array elements
          validDepts.forEach((deptId) => {
            correctedFormData.append('departments[]', deptId);
          });
          
          // Also add individual entries
          validDepts.forEach((deptId) => {
            correctedFormData.append('department[]', deptId);
          });
          
          // Add as single field if only one department
          if (validDepts.length === 1) {
            correctedFormData.append('department', validDepts[0]);
          }
          
        } else if (key === "designation") {
          const validDesigs = [...new Set(formData.designation.filter(id => id && id.trim() !== ''))];
          validDesigs.forEach((desigId) => {
            correctedFormData.append('designation[]', desigId);
            correctedFormData.append('designation', desigId);
          });
        } else if (key === "paidLeaves") {
          formData.paidLeaves.forEach((leave, index) => {
            correctedFormData.append(`paidLeaves[${index}][type]`, leave.type);
            correctedFormData.append(`paidLeaves[${index}][count]`, leave.count);
          });
        } else if (key === "documents") {
          formData.documents.forEach((doc, index) => {
            correctedFormData.append(`documents[${index}][type]`, doc.type);
            if (doc.file) {
              correctedFormData.append(`documents[${index}][file]`, doc.file);
            }
            correctedFormData.append(`documents[${index}][uploadedAt]`, doc.uploadedAt);
          });
        } else if (key === "dateOfJoining" && formData[key]) {
          correctedFormData.append(key, new Date(formData[key]).toISOString());
        } else if (key === "pfEnabled" || key === "esicEnabled") {
          correctedFormData.append(key, formData[key]);
        } else if (key === "pfPercentage" || key === "esicPercentage") {
          correctedFormData.append(key, formData[key]);
        } else if (key === "profileImage" || key === "profileImagePreview" || 
                   key === "panImage" || key === "panImagePreview" || 
                   key === "adharImage" || key === "adharImagePreview") {
          // Skip preview URLs, handle images separately
               } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          correctedFormData.append(key, formData[key]);
        }
      });
      
      // Add access permissions
      const selectedPermissions = Object.entries(userAccess)
        .filter(([_, value]) => value)
        .map(([key]) => key);
      
      selectedPermissions.forEach(permission => {
        correctedFormData.append('accessPermissions[]', permission);
      });
      
      // 🖼️ ENHANCED IMAGE HANDLING FOR DATABASE STORAGE
      console.log('🖼️ PROCESSING IMAGES FOR DATABASE STORAGE:');
      
      // Profile Image with validation
      const correctedProfileImage = processImageForDatabase(formData.profileImage, 'Profile Image (Corrected)');
      if (correctedProfileImage) {
        console.log('├── Adding validated profile image to corrected FormData');
        correctedFormData.append('profileImage', correctedProfileImage);
        correctedFormData.append('profileImageName', correctedProfileImage.name);
        correctedFormData.append('profileImageSize', correctedProfileImage.size.toString());
        correctedFormData.append('profileImageType', correctedProfileImage.type);
        correctedFormData.append('profileImageLastModified', correctedProfileImage.lastModified.toString());
      }
      
      // PAN Image with validation
      const correctedPanImage = processImageForDatabase(formData.panImage, 'PAN Image (Corrected)');
      if (correctedPanImage) {
        console.log('├── Adding validated PAN image to corrected FormData');
        correctedFormData.append('panImage', correctedPanImage);
        correctedFormData.append('panImageName', correctedPanImage.name);
        correctedFormData.append('panImageSize', correctedPanImage.size.toString());
        correctedFormData.append('panImageType', correctedPanImage.type);
        correctedFormData.append('panImageLastModified', correctedPanImage.lastModified.toString());
      }
      
      // Aadhar Image with validation (multiple field name variations for API compatibility)
      const correctedAadharImage = processImageForDatabase(formData.adharImage, 'Aadhar Image (Corrected)');
      if (correctedAadharImage) {
        console.log('├── ✅ Processing corrected Aadhar image for database storage');
        console.log('├── API expects field name: "adharImage" with type: String');
        
        // 🔴 PRIMARY API FIELD: adharImage (as specified by API schema)
        correctedFormData.append('adharImage', correctedAadharImage);
        console.log('├── ✅ Added adharImage field to corrected FormData for database storage');
        
        // Add metadata for the adharImage field
        correctedFormData.append('adharImageName', correctedAadharImage.name);
        correctedFormData.append('adharImageSize', correctedAadharImage.size.toString());
        correctedFormData.append('adharImageType', correctedAadharImage.type);
        correctedFormData.append('adharImageLastModified', correctedAadharImage.lastModified.toString());
        
        console.log('├── ✅ Corrected Aadhar image configured for API field: "adharImage" (String type)');
        console.log('├── Image details: Name=' + correctedAadharImage.name + ', Size=' + correctedAadharImage.size + ', Type=' + correctedAadharImage.type);
      } else {
        console.log('├── ❌ No Aadhar image provided or validation failed for corrected FormData');
      }
      
      console.log('└── All images processed for database storage\n');
      
      // Log corrected FormData entries
      const correctedEntries = {};
      correctedFormData.forEach((value, key) => {
        if (correctedEntries[key]) {
          if (Array.isArray(correctedEntries[key])) {
            correctedEntries[key].push(value);
          } else {
            correctedEntries[key] = [correctedEntries[key], value];
          }
        } else {
          correctedEntries[key] = value;
        }
      });
      
      console.log('🔄 CORRECTED FORMDATA ENTRIES:');
      console.log('├── Total entries:', Object.keys(correctedEntries).length);
      
      // Show department entries
      const deptEntries = Object.entries(correctedEntries).filter(([key]) => 
        key.toLowerCase().includes('department')
      );
      console.log('├── Department entries found:', deptEntries.length);
      deptEntries.forEach(([key, value]) => {
        console.log(`│   ├── ${key}: ${value}`);
      });
      
      // Show image entries with special focus on Aadhar image
      const imageEntries = Object.entries(correctedEntries).filter(([key]) => 
        key.toLowerCase().includes('image')
      );
      console.log('├── Image entries found:', imageEntries.length);
      
      // Specifically check for Aadhar image entries
      const aadharEntries = imageEntries.filter(([key]) => 
        key.toLowerCase().includes('adhar') || key.toLowerCase().includes('aadhar')
      );
      console.log('├── 🔴 CORRECTED AADHAR IMAGE ENTRIES FOUND:', aadharEntries.length);
      
      imageEntries.forEach(([key, value]) => {
        const displayValue = value instanceof File ? `[FILE: ${value.name}]` : value;
        const isAadhar = key.toLowerCase().includes('adhar') || key.toLowerCase().includes('aadhar');
        const prefix = isAadhar ? '🔴 AADHAR' : '│  ';
        console.log(`${prefix} ├── ${key}: ${displayValue}`);
      });
      
      console.log('└── Corrected FormData ready for submission\n');
      
      response = await axios({
        method,
        url,
        data: correctedFormData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        validateStatus: function (status) {
          return status >= 200 && status < 500;
        }
      });
    }

    console.log('📊 API RESPONSE STATUS:', response.status);
    console.log('📊 API RESPONSE DATA:', response.data);
    
    if (response.status >= 400) {
      console.error('❌ API ERROR RESPONSE:', {
        status: response.status,
        data: response.data,
        message: response.data?.message,
        errors: response.data?.errors,
        details: response.data?.details
      });
      
      let errorMessage = response.data?.message || `Server responded with status ${response.status}`;
      if (response.data?.errors) {
        const errorDetails = Object.entries(response.data.errors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('; ');
        errorMessage += ` - ${errorDetails}`;
      }
      
      throw new Error(errorMessage);
    }

    
    // 🔴 VERIFY AADHAR IMAGE STORAGE IN DATABASE (API field: adharImage)
    console.log('🔴 VERIFYING AADHAR IMAGE STORAGE IN DATABASE:');
    console.log('├── Checking for API field: "adharImage" (type: String)');
    
    if (response.data.adharImage) {
      console.log('├── ✅ AADHAR IMAGE SUCCESSFULLY STORED IN DATABASE!');
      console.log('├── Field: "adharImage" = ' + response.data.adharImage);
      console.log('├── Type: ' + typeof response.data.adharImage);
    } else {
      console.log('├── ❌ AADHAR IMAGE NOT FOUND in response data');
      console.log('├── Expected field: "adharImage" (type: String)');
      console.log('├── Available response fields:', Object.keys(response.data));
      
      // Check for any image-related fields in response
      const imageFields = Object.keys(response.data).filter(key => 
        key.toLowerCase().includes('image') || 
        key.toLowerCase().includes('adhar') ||
        key.toLowerCase().includes('aadhar')
      );
      console.log('├── Image/Aadhar-related fields in response:', imageFields);
    }
    
    // Log the departments that were actually saved
    if (response.data.departments) {
      const savedDeptNames = response.data.departments.map(deptId => {
        const dept = departments.find(d => d._id === deptId);
        return dept ? dept.name : `Unknown (${deptId})`;
      });
    }

    if (response.data.designation) {
    const savedDesigNames = response.data.designation.map(desigId => {
      const desig = designations.find(d => d._id === desigId);
      return desig ? desig.name : `Unknown (${desigId})`;
    });
  }
    
    setMessage(`✅ Employee ${isEditing ? "updated" : "added"} successfully!`);

    // Refresh employee list (works for both creation and editing)
    await fetchEmployees();

    // Reset form and clear states
    setFormData(emptyFormData);
    setValidationErrors({
      mobileNumber: '',
      emergencyMobileNumber: '',
      aadharNumber: '',
      panNumber: '',
      userUpi: '',
      salary: '',
      teamMemberName: ''
    });
    setEditingId(null);
    setShowForm(false);

    // **NECESSARY CHANGE: Page refresh ONLY after employee CREATION (not editing/updating)**
    if (!isEditing) {
      console.log('🔄 Triggering page refresh after successful employee creation...');
      // Brief delay to show success message to user
      setTimeout(() => {
        window.location.reload();
      }, 1500); // 1.5 seconds delay for user feedback
    }

  } catch (err) {
    // Enhanced error logging
    console.error('Full error object:', err);
    console.error('Error response data:', err.response?.data);
    console.error('Error response status:', err.response?.status);
    console.error('Error response headers:', err.response?.headers);

    console.error('Error details:', {
      message: err.message,
      response: {
        data: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers,
        request: {
          method: err.config?.method,
          url: err.config?.url,
          headers: err.config?.headers ? {
            ...err.config.headers,
            'Authorization': '[REDACTED]' // Hide token for security
          } : undefined,
          data: err.config?.data ? 'Form data sent (see below)' : undefined
        }
      }
    });

    // Log the complete form data that was sent
    try {
      const formDataEntries = {};
      if (err.config?.data instanceof FormData) {
        for (let [key, value] of err.config.data.entries()) {
          formDataEntries[key] = value;
        }
      }
      console.log('Complete form data sent:', formDataEntries);
    } catch (formDataError) {
      console.error('Could not log form data:', formDataError);
    }

    let errorMessage = "An unknown error occurred";

    if (err.code === "ERR_NETWORK") {
      errorMessage = "Unable to connect to the server. Please check your internet connection.";
    } else if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
      if (err.response.data.errors) {
        // Handle validation errors
        const errorMessages = [];
        for (const [field, errors] of Object.entries(err.response.data.errors)) {
          if (Array.isArray(errors)) {
            errorMessages.push(`${field}: ${errors.join(', ')}`);
          } else {
            errorMessages.push(`${field}: ${errors}`);
          }
        }
        errorMessage += ": " + errorMessages.join('; ');
      }
    } else if (err.message) {
      errorMessage = err.message;
    }

    setMessage(`❌ Failed to ${editingId ? "update" : "add"} employee: ${errorMessage}`);
  }
};


const handleEdit = async (employee) => {

  let processedDepartments = [];
  
  if (Array.isArray(employee.departments) && employee.departments.length > 0) {
    processedDepartments = [...employee.departments]; // Copy array
  } else if (employee.department) {
    // Handle both array and single value for legacy support
    if (Array.isArray(employee.department)) {
      processedDepartments = [...employee.department];
    } else {
      processedDepartments = [employee.department]; // Convert single to array
    }
  } else {
    processedDepartments = []; // No departments
  }
  
  // Convert to strings first to handle ObjectIds or non-strings
  const stringIds = processedDepartments.map(id => String(id).trim()).filter(idStr => idStr && idStr !== '');
  processedDepartments = [...new Set(stringIds)]; // Unique strings

  const deptNames = processedDepartments.map(deptId => {
    const dept = departments.find(d => d._id.toString() === deptId); // Safe comparison
    return dept ? dept.name : `Unknown (${deptId})`;
  });


    let processedDesignations = [];
  
  if (Array.isArray(employee.designation) && employee.designation.length > 0) {
    processedDesignations = [...employee.designation]; // Copy array
    console.log('├── Using designation array (modern format)');
  } else if (employee.designation) {
    // Handle legacy single value
    processedDesignations = [employee.designation]; // Convert single to array
    console.log('├── Converting single designation to array (legacy format)');
  } else {
    processedDesignations = []; // No designations
    console.log('├── No designations found, using empty array');
  }

  // Remove any invalid/empty entries and ensure unique IDs (with safe string conversion)
  const stringDesigIds = processedDesignations.map(id => String(id).trim()).filter(idStr => idStr && idStr !== '');
  processedDesignations = [...new Set(stringDesigIds)]; // Unique strings
  
  const desigNames = processedDesignations.map(desigId => {
    const desig = designations.find(d => d._id.toString() === desigId); // Safe comparison
    return desig ? desig.name : `Unknown (${desigId})`;
  });


  

  // Set form data
  setFormData({
    company: employee.company?._id || employee.company || "",
    teamMemberName: employee.teamMemberName || "",
    mobileNumber: employee.mobileNumber || "",
    emergencyMobileNumber: employee.emergencyMobileNumber || "",
    email: employee.email || "",
    password: "",
    salary: employee.salary || "",
    pfEnabled: employee.pfEnabled || false,
    pfPercentage: employee.pfPercentage || 12,
    esicEnabled: employee.esicEnabled || false,
    esicPercentage: employee.esicPercentage || 3.25,
    dateOfJoining: employee.dateOfJoining ? new Date(employee.dateOfJoining).toISOString().split("T")[0] : "",
    shift: employee.shift || "",
    departments: processedDepartments, // Always array of strings
    designation: processedDesignations,
    role: employee.role || "",
    aadharNumber: employee.aadharNumber || "",
    adharImage: employee.adharImage || null,
    adharImagePreview: employee.adharImage ? `${employee.adharImage}?${new Date().getTime()}` : null,
    panNumber: employee.panNumber || "",
    userUpi: employee.userUpi || "",
    profileImage: employee.profileImage || null,
    profileImagePreview: employee.profileImage ? `${employee.profileImage}?${new Date().getTime()}` : null,
    panImage: employee.panImage || null,
    panImagePreview: employee.panImage ? `${employee.panImage}?${new Date().getTime()}` : null,
    weeklyHoliday: employee.weeklyHoliday || [],
    address: employee.address || "",
    // Handle new fields from API specification
    paidLeaves: employee.paidLeaves || [],
    documents: employee.documents || [],
  });

  // Set the company ID for department filtering
  setSelectedCompanyId(employee.company?._id || employee.company || "");
  // Clear validation errors when editing
  setValidationErrors({
    mobileNumber: '',
    emergencyMobileNumber: '',
    aadharNumber: '',
    panNumber: '',
    userUpi: '',
    salary: '',
    teamMemberName: ''
  });

  // Enhanced access permissions handling with multiple fallbacks
  let accessPermissions = null;

  // Try different possible field names for access permissions
  if (employee.userAccess && typeof employee.userAccess === 'object') {
    accessPermissions = employee.userAccess;
    console.log('✅ Found userAccess field:', accessPermissions);
  } else if (employee.accessPermissions) {
    // Handle array format (like ['dashboard', 'leads_dashboard'])
    if (Array.isArray(employee.accessPermissions)) {
      console.log('📋 Processing accessPermissions array:', employee.accessPermissions);
      accessPermissions = {};
      // Initialize all permissions to false
      Object.keys(userAccess).forEach(key => {
        accessPermissions[key] = false;
      });
      // Set selected permissions to true
      employee.accessPermissions.forEach(permission => {
        if (accessPermissions.hasOwnProperty(permission)) {
          accessPermissions[permission] = true;
        }
      });
      console.log('✅ Converted array to object:', accessPermissions);
    } else if (typeof employee.accessPermissions === 'object') {
      accessPermissions = employee.accessPermissions;
      console.log('✅ Found accessPermissions object:', accessPermissions);
    }
  } else if (employee.permissions) {
    accessPermissions = employee.permissions;
    console.log('✅ Found permissions field:', accessPermissions);
  } else if (employee.access) {
    accessPermissions = employee.access;
    console.log('✅ Found access field:', accessPermissions);
  }

  // If we found access permissions, set them
  if (accessPermissions) {
    // Reset all permissions to false first
    const resetPermissions = {};
    Object.keys(userAccess).forEach(key => {
      resetPermissions[key] = false;
    });

    // Apply the found permissions
    const finalPermissions = { ...resetPermissions, ...accessPermissions };
    setUserAccess(finalPermissions);
  } else {
    console.log('⚠️ No access permissions found, resetting to default (all false)');
    // Reset all permissions to false
    const resetPermissions = {};
    Object.keys(userAccess).forEach(key => {
      resetPermissions[key] = false;
    });
    setUserAccess(resetPermissions);
  }

  // If the employee data doesn't have complete access permissions, try to fetch them from API
  if (!accessPermissions && employee._id) {
    console.log('🔍 FETCHING COMPLETE EMPLOYEE DATA FROM API...');
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        const response = await axios.get(
          `https://customersupport-jhy9.onrender.com/api/employees/${employee._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const completeEmployee = response.data;

        // Try to get access permissions from the complete data
        let apiAccessPermissions = null;
        if (completeEmployee.userAccess) {
          apiAccessPermissions = completeEmployee.userAccess;
        } else if (completeEmployee.accessPermissions) {
          if (Array.isArray(completeEmployee.accessPermissions)) {
            apiAccessPermissions = {};
            Object.keys(userAccess).forEach(key => {
              apiAccessPermissions[key] = false;
            });
            completeEmployee.accessPermissions.forEach(permission => {
              if (apiAccessPermissions.hasOwnProperty(permission)) {
                apiAccessPermissions[permission] = true;
              }
            });
          } else {
            apiAccessPermissions = completeEmployee.accessPermissions;
          }
        }

        if (apiAccessPermissions) {
          const resetPermissions = {};
          Object.keys(userAccess).forEach(key => {
            resetPermissions[key] = false;
          });
          const finalApiPermissions = { ...resetPermissions, ...apiAccessPermissions };
          setUserAccess(finalApiPermissions);
          console.log('├── Applied API permissions:', finalApiPermissions);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching complete employee data:', error);
      console.error('├── Error details:', error.response?.data || error.message);
    }
  }

  setEditingId(employee._id);
  setShowForm(true);
  window.scrollTo({ top: 0, behavior: 'smooth' });
};


  const handleDelete = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setMessage("❌ Access token not found. Please log in.");
      setShowDeleteModal(false);
      return;
    }

    try {
      await axios.delete(`https://customersupport-jhy9.onrender.com/api/employees/${employeeToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Employee deleted successfully");
      setMessage("✅ Employee deleted successfully!");
      fetchEmployees();
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
    } catch (error) {
      const errorMessage =
        error.code === "ERR_NETWORK"
          ? "Unable to connect to the server. This may be due to a CORS issue or the server being down."
          : error.response?.data?.message || error.message || "Unknown error";
      console.error("Error deleting employee:", {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      });

      setMessage(`❌ Error deleting employee: ${errorMessage}`);
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
    }
  };

  const openDeleteModal = (id) => {
    setEmployeeToDelete(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
  };

  const toggleForm = () => {
    const resetForm = () => {
      setFormData(emptyFormData);
      setValidationErrors({
        mobileNumber: '',
        emergencyMobileNumber: '',
        aadharNumber: '',
        panNumber: '',
        userUpi: '',
        salary: '',
        teamMemberName: ''
      });
      setEditingId(null);
      setShowForm(false);
      setMessage("");
    };
    if (showForm) {
      resetForm();
    }
    setShowForm(!showForm);
  };


  const renderDepartmentSelection = () => (
    <div className="mb-4">
      <label className="block text-gray-700 text-sm font-bold mb-2">
        Departments * <span className="text-sm font-normal text-gray-500">(Select multiple)</span>
      </label>
      <div className="border rounded p-3 max-h-48 overflow-y-auto bg-white">
        {!selectedCompanyId ? (
          <p className="text-gray-500 text-sm">Please select a company first</p>
        ) : departments.length === 0 ? (
          <p className="text-gray-500 text-sm">No departments available</p>
        ) : (
          <div className="space-y-2">
            {departments.map((dept) => (
              <label key={dept._id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                <input
                  type="checkbox"
                  value={dept._id}
                  checked={formData.departments.includes(dept._id)}
                  onChange={handleDepartmentChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-gray-700 text-sm">{dept.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {formData.departments.length > 0 && (
        <div className="mt-2">
          <p className="text-sm text-gray-600">Selected: {formData.departments.length} department(s)</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {formData.departments.map((deptId) => {
              const dept = departments.find(d => d._id === deptId);
              return dept ? (
                <span key={deptId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {dept.name}
                  <button
                    type="button"
                    onClick={() => handleDepartmentChange({ target: { value: deptId, checked: false } })}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-600 focus:outline-none"
                  >
                    ×
                  </button>
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );

const renderDesignationDropdown = () => (
  <div className="mb-4">
    <label className="block text-gray-700 text-sm font-bold mb-2">
      Designations * <span className="text-sm font-normal text-gray-500">(Select multiple)</span>
    </label>
    <div className="border rounded p-3 max-h-48 overflow-y-auto bg-white">
      {!selectedCompanyId ? (
        <p className="text-gray-500 text-sm">Please select a company first</p>
      ) : formData.departments.length === 0 ? (
        <p className="text-gray-500 text-sm">Please select at least one department first</p>
      ) : designations.length === 0 ? (
        <p className="text-gray-500 text-sm">No designations available for selected departments</p>
      ) : (
        <div className="space-y-2">
          {designations.map((desig) => (
            <label key={desig._id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                value={desig._id}
                checked={formData.designation.includes(desig._id)}
                onChange={handleDesignationChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="text-gray-700 text-sm">{desig.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
    {formData.designation.length > 0 && (
      <div className="mt-2">
        <p className="text-sm text-gray-600">Selected: {formData.designation.length} designation(s)</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {formData.designation.map((desigId) => {
            const desig = designations.find(d => d._id === desigId);
            return desig ? (
              <span key={desigId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                {desig.name}
                <button
                  type="button"
                  onClick={() => handleDesignationChange({ target: { value: desigId, checked: false } })}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-600 focus:outline-none"
                >
                  ×
                </button>
              </span>
            ) : null;
          })}
        </div>
      </div>
    )}
  </div>
);


const filteredEmployees = employees.filter(employee => {
  const matchesSearch =
    employee.teamMemberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.mobileNumber?.includes(searchTerm) ||
    employee.role?.toLowerCase().includes(searchTerm.toLowerCase());

  // Handle department filter: check if filter ID is in array (or legacy single)
  let matchesDepartment = !departmentFilter;
  if (departmentFilter) {
    const employeeDepts = Array.isArray(employee.departments) ? employee.departments : (employee.department ? [employee.department] : []);
    matchesDepartment = employeeDepts.includes(departmentFilter);
  }

  return matchesSearch && matchesDepartment;
});


  const handleView = (employee) => {
    setViewingEmployee(employee);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewingEmployee(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading employees...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Employee Management</h1>
            <p className="text-gray-600 mt-2">Manage all employees in your system</p>
          </div>
          <button
            onClick={toggleForm}
            className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                clipRule="evenodd"
              />
            </svg>
            {showForm ? "Cancel" : "Add Employee"}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`p-4 rounded-md mb-6 ${
              message.includes("✅") ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            }`}
          >
            {message}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this employee?</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Employee Modal */}
        {isViewModalOpen && viewingEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800">Employee Details</h3>
                  <button
                    onClick={closeViewModal}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="h-24 w-24 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
                        {viewingEmployee.profileImage ? (
                          <img 
                            src={`${viewingEmployee.profileImage}?${new Date().getTime()}`} 
                            alt={viewingEmployee.teamMemberName || 'Profile'} 
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span className={`text-3xl text-indigo-600 font-semibold ${viewingEmployee.profileImage ? 'hidden' : 'flex'}`}>
                          {viewingEmployee.teamMemberName?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-gray-800">{viewingEmployee.teamMemberName || 'N/A'}</h4>
                        <p className="text-gray-600">{viewingEmployee.role || 'N/A'}</p>
                        <p className="text-sm text-gray-500">
                          {departments.find(d => d._id === viewingEmployee.department)?.name || 'N/A'}
                        </p>
                        {viewingEmployee.designation && (
                          <p className="text-sm text-gray-500">
                            {typeof viewingEmployee.designation === 'object' 
                              ? viewingEmployee.designation.name 
                              : designations.find(d => d._id === viewingEmployee.designation)?.name || viewingEmployee.designation
                            }
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                      <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">
                        <svg className="h-4 w-4 inline-block mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Personal Information
                      </h5>
                      <div className="space-y-2">
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Full Name:</p>
                          <p className="text-sm font-medium text-gray-800 flex-1">{viewingEmployee.teamMemberName || 'N/A'}</p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Email:</p>
                          <p className="text-sm text-blue-600 flex-1">{viewingEmployee.email || 'N/A'}</p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Mobile:</p>
                          <p className="text-sm text-gray-800 flex-1">{viewingEmployee.mobileNumber || 'N/A'}</p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Emergency Contact:</p>
                          <p className="text-sm text-gray-800 flex-1">{viewingEmployee.emergencyMobileNumber || 'N/A'}</p>
                        </div>
                        {viewingEmployee.address && (
                          <div className="flex">
                            <p className="text-sm text-gray-500 w-32">Address:</p>
                            <p className="text-sm text-gray-800 flex-1">{viewingEmployee.address}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Work Information */}
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                      <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">
                        <svg className="h-4 w-4 inline-block mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Work Information
                      </h5>
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Employee ID:</p>
                          <p className="text-sm font-medium text-gray-800 flex-1">{viewingEmployee._id?.substring(0, 8) || 'N/A'}</p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Date of Joining:</p>
                          <p className="text-sm text-gray-800 flex-1">
                            {viewingEmployee.dateOfJoining ? new Date(viewingEmployee.dateOfJoining).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Department:</p>
                          <p className="text-sm text-gray-800 flex-1">
                            {departments.find(d => d._id === viewingEmployee.department)?.name || 'N/A'}
                          </p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Designation:</p>
                          <p className="text-sm text-gray-800 flex-1">
                            {typeof viewingEmployee.designation === 'object' 
                              ? viewingEmployee.designation.name 
                              : designations.find(d => d._id === viewingEmployee.designation)?.name || viewingEmployee.designation || 'N/A'
                            }
                          </p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Role:</p>
                          <p className="text-sm text-gray-800 flex-1">
                            {viewingEmployee.role || 'N/A'}
                          </p>
                        </div>
                        <div className="flex">
                          <p className="text-sm text-gray-500 w-32">Shift:</p>
                          <p className="text-sm text-gray-800 flex-1">
                            {viewingEmployee.shift || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Document Information */}
                    <div className="space-y-4">
                      <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                        <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">
                          <svg className="h-4 w-4 inline-block mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Document Information
                        </h5>
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex">
                            <p className="text-sm text-gray-500 w-32">PAN Number:</p>
                            <p className="text-sm text-gray-800 flex-1">{viewingEmployee.panNumber || 'N/A'}</p>
                          </div>
                          {viewingEmployee.aadharNumber && (
                            <div className="flex">
                              <p className="text-sm text-gray-500 w-32">Aadhar Number:</p>
                              <p className="text-sm text-gray-800 flex-1">{viewingEmployee.aadharNumber}</p>
                            </div>
                          )}
                          {viewingEmployee.userUpi && (
                            <div className="flex">
                              <p className="text-sm text-gray-500 w-32">UPI ID:</p>
                              <p className="text-sm text-gray-800 flex-1">{viewingEmployee.userUpi}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Document Images */}
                      <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                        <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">
                          <svg className="h-4 w-4 inline-block mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Document Images
                        </h5>
                        <div className="flex flex-wrap gap-4">
                          {viewingEmployee.panImage && (
                            <div className="text-center">
                              <button
                                onClick={() => setIsPanImageModalOpen(true)}
                                className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                              >
                                <img 
                                  src={`${viewingEmployee.panImage}?${new Date().getTime()}`}
                                  alt="PAN Card"
                                  className="max-w-full max-h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                                  }}
                                />
                              </button>
                              <p className="text-xs text-gray-500 mt-1">PAN Card</p>
                            </div>
                          )}
                          
                          {viewingEmployee.adharImage && (
                            <div className="text-center">
                              <button
                                onClick={() => setIsAadharImageModalOpen(true)}
                                className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                              >
                                <img 
                                  src={`${viewingEmployee.adharImage}?${new Date().getTime()}`}
                                  alt="Aadhar Card"
                                  className="max-w-full max-h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                                  }}
                                />
                              </button>
                              <p className="text-xs text-gray-500 mt-1">Aadhar Card</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Access Permissions */}
                      <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                        <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">
                          <svg className="h-4 w-4 inline-block mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Access Permissions
                        </h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2">
                          {(() => {
                            // Get permissions from multiple possible sources
                            const permissions = viewingEmployee.userAccess || 
                                              viewingEmployee.accessPermissions || 
                                              viewingEmployee.permissions || 
                                              viewingEmployee.access || {};
                            
                            // Handle array format
                            let permissionsObj = permissions;
                            if (Array.isArray(permissions)) {
                              permissionsObj = {};
                              permissions.forEach(permission => {
                                permissionsObj[permission] = true;
                              });
                            }
                            
                            const activePermissions = Object.entries(permissionsObj).filter(([key, value]) => value);
                            
                            if (activePermissions.length === 0) {
                              return (
                                <p className="text-sm text-gray-500 italic col-span-2">No special permissions assigned</p>
                              );
                            }
                            
                            return activePermissions.map(([key, value]) => (
                              <div key={key} className="flex items-center">
                                <svg className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700 capitalize">
                                  {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                {viewingEmployee.address && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Address</h5>
                    <p className="text-gray-700">{viewingEmployee.address}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap gap-3 justify-between">
                  {/* <div className="flex flex-wrap gap-3">
                    {viewingEmployee.panImage && (
                      <button
                        onClick={() => setIsPanImageModalOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        View PAN Image
                      </button>
                    )}
                    {viewingEmployee.adharImage && (
                      <button
                        onClick={() => setIsAadharImageModalOpen(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Aadhar Card
                      </button>
                    )}
                  </div> */}
                  <div className="space-x-3">
                    <button
                      onClick={closeViewModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        closeViewModal();
                        handleEdit(viewingEmployee);
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Edit Profile
                    </button>
                  </div>
                </div>
              </div>
              
              {/* <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end space-x-3">
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closeViewModal();
                    handleEdit(viewingEmployee);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Edit Profile
                </button>
              </div> */}
            </div>
          </div>
        )}
        
        {/* PAN Image Modal */}
        {/* PAN Image Modal */}
        {isPanImageModalOpen && viewingEmployee?.panImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">PAN Card</h3>
                  <button
                    onClick={() => setIsPanImageModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-center bg-gray-100 p-4 rounded-lg">
                  <img 
                    src={`${viewingEmployee.panImage}?${new Date().getTime()}`}
                    alt="PAN Card"
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/500x300?text=Image+Not+Available';
                    }}
                  />
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setIsPanImageModalOpen(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aadhar Image Modal */}
        {isAadharImageModalOpen && viewingEmployee?.adharImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">Aadhar Card</h3>
                  <button
                    onClick={() => setIsAadharImageModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-center bg-gray-100 p-4 rounded-lg">
                  <img 
                    src={`${viewingEmployee.adharImage}?${new Date().getTime()}`}
                    alt="Aadhar Card"
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/500x300?text=Image+Not+Available';
                    }}
                  />
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setIsAadharImageModalOpen(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Add/Edit Employee Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              {editingId ? "Edit Employee" : "Add New Employee"}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6" encType="application/json">
              {/* Personal Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team Member Name *</label>
                  <input
                    type="text"
                    name="teamMemberName"
                    value={formData.teamMemberName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Employee Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                  <input
                    type="text"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Mobile Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Mobile Number</label>
                  <input
                    type="text"
                    name="emergencyMobileNumber"
                    value={formData.emergencyMobileNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Emergency Mobile Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Email Address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {editingId ? "(Optional)" : "*"}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingId}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={editingId ? "Leave blank to keep current" : "Set a password"}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="profileImage">
                    Profile Image
                  </label>
                  <div className="flex items-center">
                    <div className="relative">
                      {formData.profileImagePreview ? (
                        <div className="relative">
                          <img 
                            src={formData.profileImagePreview} 
                            alt="Profile Preview" 
                            className="h-20 w-20 rounded-full object-cover border-2 border-gray-300"
                          />
                          <label 
                            htmlFor="profileImage"
                            className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-1 cursor-pointer hover:bg-blue-600 transition-colors"
                            title="Change photo"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </label>
                        </div>
                      ) : (
                        <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      )}
                      <input
                        type="file"
                        id="profileImage"
                        name="profileImage"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </div>
                    <div className="ml-4">
                      <label 
                        htmlFor="profileImage"
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                      >
                        {formData.profileImagePreview ? 'Change' : 'Upload'} Photo
                      </label>
                      {formData.profileImagePreview && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, profileImage: null, profileImagePreview: null }))}
                          className="ml-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md shadow-sm text-sm leading-4 font-medium text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    JPG, GIF or PNG. Max size 2MB
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="panImage">
                    PAN Card Image
                  </label>
                  <div className="flex items-center">
                    <div className="relative">
                      {formData.panImagePreview ? (
                        <div className="relative">
                          <img 
                            src={formData.panImagePreview} 
                            alt="PAN Card Preview" 
                            className="h-32 w-48 object-cover border-2 border-gray-300 rounded"
                          />
                          <label 
                            htmlFor="panImage"
                            className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-1 cursor-pointer hover:bg-blue-600 transition-colors"
                            title="Change PAN image"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </label>
                        </div>
                      ) : (
                        <div className="h-32 w-48 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                          <div className="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-xs text-gray-500 mt-1">Upload PAN Card</p>
                          </div>
                        </div>
                      )}
                      <input
                        type="file"
                        id="panImage"
                        name="panImage"
                        accept="image/*"
                        onChange={handlePanImageChange}
                        className="hidden"
                      />
                    </div>
                    <div className="ml-4">
                      <label 
                        htmlFor="panImage"
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                      >
                        {formData.panImagePreview ? 'Change' : 'Upload'} PAN
                      </label>
                      {formData.panImagePreview && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, panImage: null, panImagePreview: null }))}
                          className="ml-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md shadow-sm text-sm leading-4 font-medium text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    JPG, GIF or PNG. Max size 2MB
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="adharImage">
                    Aadhar Card Image
                  </label>
                  <div className="flex items-center">
                    <div className="relative">
                      {formData.adharImagePreview ? (
                        <div className="relative">
                          <img 
                            src={formData.adharImagePreview} 
                            alt="Aadhar Card Preview" 
                            className="h-32 w-48 object-cover border-2 border-gray-300 rounded"
                          />
                          <label 
                            htmlFor="adharImage"
                            className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-1 cursor-pointer hover:bg-blue-600 transition-colors"
                            title="Change Aadhar image"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </label>
                        </div>
                      ) : (
                        <div className="h-32 w-48 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                          <div className="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-xs text-gray-500 mt-1">Upload Aadhar Card</p>
                          </div>
                        </div>
                      )}
                      <input
                        type="file"
                        id="adharImage"
                        name="adharImage"
                        accept="image/*"
                        onChange={handleAadharImageChange}
                        className="hidden"
                      />
                    </div>
                    <div className="ml-4">
                      <label 
                        htmlFor="adharImage"
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                      >
                        {formData.adharImagePreview ? 'Change' : 'Upload'} Aadhar
                      </label>
                      {formData.adharImagePreview && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, adharImage: null, adharImagePreview: null }))}
                          className="ml-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md shadow-sm text-sm leading-4 font-medium text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    JPG, GIF or PNG. Max size 2MB
                  </p>
                </div>

              </div>

              {/* Job & Work Information */}
              <div className="space-y-4">
                {renderDepartmentSelection()}
                {renderDesignationDropdown()}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
                  <input
                    type="text"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Salary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Joining</label>
                  <input
                    type="date"
                    name="dateOfJoining"
                    value={formData.dateOfJoining}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="shift">
                    Shift <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="shift"
                    name="shift"
                    value={formData.shift}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  >
                    <option value="">Select Shift</option>
                    {shifts.map((shift) => (
                      <option key={shift._id} value={shift.name}>
                        {shift.name} ({shift.startTime} - {shift.endTime})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Holiday</label>
                  <div className="flex flex-wrap gap-4">
                    {days.map((day) => (
                      <label key={day} className="flex items-center">
                        <input
                          type="checkbox"
                          name="weeklyHoliday"
                          value={day}
                          checked={formData.weeklyHoliday.includes(day)}
                          onChange={handleDayChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Identity & Address */}
              <div className="space-y-4 md:col-span-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                  <input
                    type="text"
                    name="aadharNumber"
                    value={formData.aadharNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Aadhar Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="PAN Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User UPI</label>
                  <input
                    type="text"
                    name="userUpi"
                    value={formData.userUpi}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="User UPI"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Address"
                  ></textarea>
                </div>
              </div>

              {/* User Access Control */}
              <div className="space-y-4 md:col-span-2">
                <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Access Permissions</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 

                  {/* Team Member */}
                  {/* <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between border-b pb-2 mb-2">
                      <h4 className="font-medium text-gray-800">Team Member</h4>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={isSectionAllSelected(['list_teammember', 'create_teammember', 'update_teammember', 'delete_teammember'])}
                          ref={(el) => {
                            if (el) el.indeterminate = isSectionPartiallySelected(['list_teammember', 'create_teammember', 'update_teammember', 'delete_teammember']);
                          }}
                          onChange={(e) => handleSectionSelectAll(['list_teammember', 'create_teammember', 'update_teammember', 'delete_teammember'], e.target.checked)}
                          className="form-checkbox h-4 w-4 text-blue-600 rounded"
                          title="Select All Team Member Permissions"
                        />
                        <span className="text-xs text-gray-500">All</span>
                      </label>
                    </div>
                    {['list_teammember', 'create_teammember', 'update_teammember', 'delete_teammember'].map(permission => (
                      <label key={permission} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name={permission}
                          checked={userAccess[permission]}
                          onChange={handlePermissionChange}
                          className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {permission.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </span>
                      </label>
                    ))}
                  </div> */}

              

                  {/* Department */}
                  {/* <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between border-b pb-2 mb-2">
                      <h4 className="font-medium text-gray-800">Department</h4>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={isSectionAllSelected(['list_department', 'create_department', 'update_department', 'delete_department'])}
                          ref={(el) => {
                            if (el) el.indeterminate = isSectionPartiallySelected(['list_department', 'create_department', 'update_department', 'delete_department']);
                          }}
                          onChange={(e) => handleSectionSelectAll(['list_department', 'create_department', 'update_department', 'delete_department'], e.target.checked)}
                          className="form-checkbox h-4 w-4 text-blue-600 rounded"
                          title="Select All Department Permissions"
                        />
                        <span className="text-xs text-gray-500">All</span>
                      </label>
                    </div>
                    {['list_department', 'create_department', 'update_department', 'delete_department'].map(permission => (
                      <label key={permission} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name={permission}
                          checked={userAccess[permission]}
                          onChange={handlePermissionChange}
                          className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {permission.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </span>
                      </label>
                    ))}
                  </div> */}

                 
      {/* Customer Support */}
<div className="space-y-2 p-4 bg-gray-50 rounded-lg">
  <div className="flex items-center justify-between border-b pb-2 mb-2">
    <h4 className="font-medium text-gray-800">Customer Support</h4>
    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={isSectionAllSelected(['customer_support', 'com_tickets', 'employee_performance'])}
        ref={(el) => {
          if (el) el.indeterminate = isSectionPartiallySelected(['customer_support', 'com_tickets', 'employee_performance']);
        }}
        onChange={(e) =>
          handleSectionSelectAll(['customer_support', 'com_tickets', 'employee_performance'], e.target.checked)
        }
        className="form-checkbox h-4 w-4 text-blue-600 rounded"
        title="Select All Customer Support Permissions"
      />
      <span className="text-xs text-gray-500">All</span>
    </label>
  </div>

  {['customer_support', 'com_tickets', 'employee_performance'].map(permission => {
    const displayNames = {
      customer_support: "Raise Ticket",
      com_tickets: "Assign Tickets",
      employee_performance: "Employee Performance",
    };

    return (
      <label key={permission} className="flex items-center space-x-2">
        <input
          type="checkbox"
          name={permission}
          checked={userAccess[permission]}
          onChange={handlePermissionChange}
          className="form-checkbox h-4 w-4 text-indigo-600 rounded"
        />
        <span className="text-sm text-gray-700">
          {displayNames[permission] || permission}
        </span>
      </label>
    );
  })}
</div>


                </div>
              </div>
              {/* Form Actions */}
              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  {editingId ? "Update Employee" : "Create Employee"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Employees List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Employee List</h2>
                <p className="text-gray-600 text-sm">{filteredEmployees.length} employees found</p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search employees..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm rounded-md"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {employees.length === 0 ? (
            <div className="p-12 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m2-0h-2m0-3h.01M17 18h.01M14 18h.01M7 18h.01M7 21h.01M7 24h.01M11 21h.01M11 24h.01M15 21h.01M15 24h.01"
                />
              </svg>
              <p className="mt-4 text-gray-600">No employees found. Add your first employee to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Mobile
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Department
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEmployees.map((employee, index) => (
                    <tr key={employee._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.teamMemberName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.mobileNumber || "N/A"}
                      </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  {(() => {
    const deptNames = getDepartmentNames(employee.departments || employee.department);
    if (deptNames.includes('Loading departments...')) {
      return <span className="text-xs text-gray-400">Loading...</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {deptNames.map((name, idx) => (
          <span key={idx} className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
            {name}
          </span>
        ))}
      </div>
    );
  })()}
</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            employee.role ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                          }`}>
                          {employee.role || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleView(employee)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                          title="View Details"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEdit(employee)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                          title="Edit"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openDeleteModal(employee._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-2-2V7a2 2 0 011.138-1.857l12.142.867a2 2 0 012 2z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default AddEmployee;
